import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { useContent } from "../context/ContentContext";

export default function Navbar() {
  const { content } = useContent();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const isHome = window.location.pathname === "/";

  const handleNavClick = (id) => (e) => {
    e.preventDefault();
    setMenuOpen(false);
    if (isHome) {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      window.location.href = `/#${id}`;
    }
  };

  const companyName = content?.company?.name ?? "ALPHA TRUST";

  const navItems = [
    { id: "about", label: "About us", type: "scroll" },
    { id: "projects", label: "Projects", type: "scroll" },
    { id: "contact", label: "Contact", type: "scroll" },
    { href: "/announcements", label: "Announcements", type: "link" },
  ];

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 bg-[#00025C] border-b border-white/5"
      data-testid="site-navbar"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 h-14 md:h-16 flex items-center justify-between">
        {/* Logo */}
        <a
          href="/"
          className="flex items-center hover:opacity-85 transition-opacity"
          data-testid="navbar-logo"
          aria-label={companyName}
        >
          <img
            src="/alpha-trust-logo.png"
            alt={companyName}
            className="h-6 md:h-7 w-auto"
            draggable={false}
          />
        </a>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-12 text-sm text-[#F9F8F6]">
          {navItems.map((n) =>
            n.type === "scroll" ? (
              <a
                key={n.id}
                href={`#${n.id}`}
                onClick={handleNavClick(n.id)}
                className="hover:text-[#C4A661] transition-colors"
                data-testid={`nav-link-${n.id}`}
              >
                {n.label}
              </a>
            ) : (
              <a
                key={n.href}
                href={n.href}
                className="hover:text-[#C4A661] transition-colors"
                data-testid="nav-link-announcements"
              >
                {n.label}
              </a>
            )
          )}
        </nav>

        {/* Hamburger (mobile only) */}
        <button
          onClick={() => setMenuOpen(true)}
          className="md:hidden inline-flex items-center justify-center w-10 h-10 -mr-2 text-[#F9F8F6] hover:text-[#C4A661] transition-colors"
          aria-label="Open menu"
          data-testid="navbar-menu-open"
        >
          <Menu size={22} strokeWidth={1.75} />
        </button>
      </div>

      {/* Mobile slide-out menu */}
      <div
        className={`md:hidden fixed inset-0 z-[60] transition-opacity duration-300 ${
          menuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        aria-hidden={!menuOpen}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/50"
          onClick={() => setMenuOpen(false)}
          aria-hidden
        />

        {/* Panel */}
        <div
          className={`absolute top-0 right-0 h-full w-[84%] max-w-sm bg-[#00025C] text-[#F9F8F6] shadow-2xl transition-transform duration-[420ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
            menuOpen ? "translate-x-0" : "translate-x-full"
          }`}
          data-testid="mobile-menu-panel"
          role="dialog"
          aria-modal="true"
        >
          <div className="flex items-center justify-between h-14 px-6 border-b border-[#F9F8F6]/10">
            <img src="/alpha-trust-logo.png" alt={companyName} className="h-6 w-auto" draggable={false} />
            <button
              onClick={() => setMenuOpen(false)}
              className="inline-flex items-center justify-center w-10 h-10 -mr-2 text-[#F9F8F6] hover:text-[#C4A661] transition-colors"
              aria-label="Close menu"
              data-testid="navbar-menu-close"
            >
              <X size={22} strokeWidth={1.75} />
            </button>
          </div>

          <nav className="px-6 pt-4 pb-6">
            {navItems.map((n, i) =>
              n.type === "scroll" ? (
                <a
                  key={n.id}
                  href={`#${n.id}`}
                  onClick={handleNavClick(n.id)}
                  className="group flex items-baseline justify-between border-b border-[#F9F8F6]/12 py-5"
                  data-testid={`mobile-nav-link-${n.id}`}
                >
                  <span className="font-serif text-2xl font-semibold group-hover:translate-x-1 transition-transform">
                    {n.label}
                  </span>
                  <span className="font-sans text-[10px] text-[#F9F8F6]/55 tracking-[0.25em] tabular-nums">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </a>
              ) : (
                <a
                  key={n.href}
                  href={n.href}
                  className="group flex items-baseline justify-between border-b border-[#F9F8F6]/12 py-5"
                  data-testid="mobile-nav-link-announcements"
                  onClick={() => setMenuOpen(false)}
                >
                  <span className="font-serif text-2xl font-semibold group-hover:translate-x-1 transition-transform">
                    {n.label}
                  </span>
                  <span className="font-sans text-[10px] text-[#F9F8F6]/55 tracking-[0.25em] tabular-nums">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </a>
              )
            )}
          </nav>
        </div>
      </div>
    </header>
  );
}
